import React, { useEffect, lazy, Suspense } from 'react'
import type { RouteObject } from 'react-router-dom'
import { useRoutes } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'

import theme from './themes/theme'
import './styles/App.scss'

// Critical path - load immediately
import Layout from './components/layout'
import Home from './components/pages/Home'
import NoMatch from './components/pages/NoMatch'

// Lazy load non-critical pages for better performance
const Privacy = lazy(() => import('./components/pages/Privacy'))
const TermsOfUse = lazy(() => import('./components/pages/TermsOfUse'))
const FAQ = lazy(() => import('./components/pages/FAQ'))
const BJJ101 = lazy(() => import('./components/pages/BJJ101'))
const YoungAdults = lazy(() => import('./components/pages/YoungAdults'))
const Rules = lazy(() => import('./components/pages/Rules'))
const BlueBelt = lazy(() => import('./components/pages/BlueBelt'))
const About = lazy(() => import('./components/pages/About'))
const Courses = lazy(() => import('./components/pages/Courses'))
const Archive = lazy(() => import('./components/pages/Archive'))
const Welcome = lazy(() => import('./components/pages/Welcome'))
const Registered = lazy(() => import('./components/pages/Registered'))
const MembershipOptions = lazy(() => import('./components/pages/MembershipOptions'))
const MembershipAgreement = lazy(() => import('./components/pages/MembershipAgreement'))
const MartialArts = lazy(() => import('./components/pages/MartialArts'))
const Instructors = lazy(() => import('./components/pages/Instructors'))
const Competitions = lazy(() => import('./components/pages/Competitions'))
const CompetitionDetail = lazy(() => import('./components/pages/Competitions/CompetitionDetail'))
const CompetitionTeam = lazy(() => import('./components/pages/CompetitionTeam'))

// Location pages - create lazy wrapper components
const WestLinnLocation = lazy(() => import('./components/pages/Location').then(m => ({
  default: () => <m.LocationPage config={m.locationConfigs['west-linn']} />
})))
const TigardLocation = lazy(() => import('./components/pages/Location').then(m => ({
  default: () => <m.LocationPage config={m.locationConfigs['tigard']} />
})))
const TualatinLocation = lazy(() => import('./components/pages/Location').then(m => ({
  default: () => <m.LocationPage config={m.locationConfigs['tualatin']} />
})))
const SellwoodLocation = lazy(() => import('./components/pages/Location').then(m => ({
  default: () => <m.LocationPage config={m.locationConfigs['sellwood']} />
})))
const PortlandLocation = lazy(() => import('./components/pages/Location').then(m => ({
  default: () => <m.LocationPage config={m.locationConfigs['portland']} />
})))
const MilwaukieLocation = lazy(() => import('./components/pages/Location').then(m => ({
  default: () => <m.LocationPage config={m.locationConfigs['milwaukie']} />
})))
const OregonCityLocation = lazy(() => import('./components/pages/Location').then(m => ({
  default: () => <m.LocationPage config={m.locationConfigs['oregon-city']} />
})))
const StaffordLocation = lazy(() => import('./components/pages/Location').then(m => ({
  default: () => <m.LocationPage config={m.locationConfigs['stafford']} />
})))

// Loading fallback component
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress />
  </Box>
)

// Wrap lazy components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<any>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
)

function External (props: any) {
  window.location.href = props.to
  return null
}

function App () {
  const routes: RouteObject[] = [
    {
      path: '/',
      element: <Layout />,
      children: [
        { index: true, element: <Home /> },
        { path: 'policies/privacy', element: withSuspense(Privacy) },
        { path: 'policies/terms-of-use', element: withSuspense(TermsOfUse) },
        { path: 'faq', element: withSuspense(FAQ) },
        { path: 'enroll', element: withSuspense(MembershipOptions) },
        { path: 'enroll/*', element: withSuspense(MembershipOptions) },
        { path: 'bjj101', element: withSuspense(BJJ101) },
        { path: 'ya', element: withSuspense(YoungAdults) },
        { path: 'rules', element: withSuspense(Rules) },
        { path: 'bluebelt', element: withSuspense(BlueBelt) },
        { path: 'about', element: withSuspense(About) },
        { path: 'instructors', element: withSuspense(Instructors) },
        { path: 'courses', element: withSuspense(Courses) },
        { path: 'archive', element: withSuspense(Archive) },
        { path: 'welcome', element: withSuspense(Welcome) },
        { path: 'registered', element: withSuspense(Registered) },
        { path: 'martial-arts', element: withSuspense(MartialArts) },
        { path: 'competitions', element: withSuspense(Competitions) },
        { path: 'competitions/:id', element: withSuspense(CompetitionDetail) },
        { path: 'competition-team', element: withSuspense(CompetitionTeam) },
        { path: 'west-linn', element: withSuspense(WestLinnLocation) },
        { path: 'tigard', element: withSuspense(TigardLocation) },
        { path: 'tualatin', element: withSuspense(TualatinLocation) },
        { path: 'sellwood', element: withSuspense(SellwoodLocation) },
        { path: 'portland', element: withSuspense(PortlandLocation) },
        { path: 'milwaukie', element: withSuspense(MilwaukieLocation) },
        { path: 'oregon-city', element: withSuspense(OregonCityLocation) },
        { path: 'stafford', element: withSuspense(StaffordLocation) },
        { path: 'membership-agreement', element: withSuspense(MembershipAgreement) },
        { path: 'enroll/options', element: withSuspense(MembershipOptions) },
        { path: 'enroll/management', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/login" /> },
        { path: 'enroll/membership', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/6kzgr" /> },
        { path: 'enroll/bjj101', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/lWPne" /> },
        { path: 'enroll/daypass', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/AvRPP" /> },
        { path: 'enroll/ya', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/6POWj" /> },
        { path: 'enroll/young-athletes', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/Anmbj" /> },
        { path: 'enroll/oneweek', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/Aqzxe" /> },
        { path: 'enroll/openmat', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/lNOWL" /> },
        { path: 'enroll/all', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/6a1ZN" /> },
        { path: 'enroll/month-to-month-membership', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/lKdW0" /> },
        { path: 'enroll/one-year-membership', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/65kLg" /> },
        { path: 'enroll/ten-session-punchcard', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/A13da" /> },
        { path: 'enroll/five-session-punchcard', element: <External to="https://the-black-dog-jiu-jitsu.gymdesk.com/signup/v/65WEN" /> },
        { path: 'google-reviews', element: <External to="https://g.page/r/CZAGtbBXV1qOEAI/review" /> },
        { path: 'main-group-chat', element: <External to="https://ig.me/j/AbZHoZEUFaqGmOpT/" /> },
        { path: '*', element: <NoMatch /> }
      ]
    }
  ]

  const routesCompiled = useRoutes(routes)

  useEffect(() => {
    // Check if Intercom is loaded before calling it (it may be deferred)
    const bootIntercom = () => {
      if (typeof (window as any).Intercom === 'function') {
        (window as any).Intercom('boot', {
          api_base: 'https://api-iam.intercom.io',
          app_id: 'r6z3rvxi'
        })
      }
    }
    
    // Try immediately, or wait for it to load
    if (typeof (window as any).Intercom === 'function') {
      bootIntercom()
    } else {
      // Listen for when Intercom becomes available
      const checkIntercom = setInterval(() => {
        if (typeof (window as any).Intercom === 'function') {
          bootIntercom()
          clearInterval(checkIntercom)
        }
      }, 500)
      
      // Clean up after 10 seconds if Intercom never loads
      setTimeout(() => clearInterval(checkIntercom), 10000)
    }
  }, [])

  return (
    <ThemeProvider theme={theme}>
      {routesCompiled}
    </ThemeProvider>
  )
}

export default App
